import {
  crewWorkOrderEmail,
  bookedDay,
  documentTotal,
  toCents,
  tradesPhrase,
  type Job,
  type WorkOrder,
  crewLabel,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import {
  workOrderFilename,
  workOrderPdfBase64,
  type WorkOrderPdf,
} from "../../lib/workOrderPdf";
import { ComposeEmail } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";

/**
 * Sending the work order to the crew.
 *
 * Lysa, 2026-09-12: "When scheduling WOs and MOs I can't tell if anything was
 * sent to the crews or distributor. I'd like either a pop up like the one when
 * I send an estimate where I can actually press 'send WO/send MO'… i like the
 * first option though that way I can add a note in the email if i need to."
 *
 * So it is the estimate's composer, which is what she means by "the pop up":
 * the same From line, the same editable body, the same attachments, the same
 * Copy button for a crew who would rather have it on WhatsApp. The letter is
 * written for her and is hers to change before it goes — the note she wants to
 * add is a sentence typed into the message, not a second field.
 *
 * **It does not send itself.** The calendar invitation and the text do, because
 * a day is a fact and needs no covering letter. A document going to somebody
 * outside the company is not that, and the person booking the crew is the one
 * who knows whether this order is ready to be read.
 */
export function SendWorkOrderModal({
  order,
  items,
  job,
  crew,
  companyName,
  onClose,
  onSent,
}: {
  order: WorkOrder;
  items: NonNullable<WorkOrder["items"]>;
  job: Job;
  crew: { name?: string | null; email?: string | null } | null;
  companyName: string | null;
  onClose: () => void;
  /** Records who it went to, so the screen can say so afterwards. */
  onSent: (to: string) => Promise<void>;
}) {
  const { account } = useGmailAccount();
  const { senders, own } = useGmailSenders();

  /*
   * Whether the crew's pay is on their copy.
   *
   * On by default: a work order's money is what the crew is owed, and the
   * reason a supplier's copy hides a price does not apply to the people being
   * paid it. Off is for the copy that goes to somebody who is not the crew.
   */
  const [withPricing, setWithPricing] = useState(true);

  const day = bookedDay(order);
  const when = day
    ? new Date(day).toLocaleString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const number = `WO-${order.order_number}`;
  const filename = workOrderFilename(number);

  const letter = crewWorkOrderEmail({
    order,
    job,
    crewName: crewLabel(crew),
    companyName,
    when,
    lines: items.map((item) => ({
      description: item.description,
      qty: item.qty,
      unit: item.unit,
    })),
    attachmentName: filename,
  });

  /** The order, as the PDF wants it. Drawn when Send is pressed. */
  const pdf = (): WorkOrderPdf => ({
    number,
    crewName: crewLabel(crew),
    jobNumber: job.job_number ?? null,
    jobAddress: [job.address, job.city, job.state, job.zip]
      .filter(Boolean)
      .join(", "),
    customerName: job.contact?.full_name ?? null,
    when,
    trades: tradesPhrase(order.trades) || null,
    description: order.description ?? null,
    lines: items.map((item) => ({
      description: item.description ?? "",
      qty: Number(item.qty ?? 0),
      unit: String(item.unit ?? ""),
      unitPrice: toCents(item.unit_price ?? 0),
    })),
    total: documentTotal(order),
    withPricing,
  });

  const to = crew?.email ?? "";

  return (
    <ComposeEmail
      title={t("Send {number} to {crew}", {
        number,
        crew: crewLabel(crew) ?? t("the crew"),
      })}
      subtitle={
        when
          ? t("Booked for {when}", { when })
          : t("No day is booked yet — say so in the message, or book it first.")
      }
      to={to}
      subject={letter.subject}
      body={letter.body}
      jobId={order.job_id}
      // A crew is not the customer: this must not tick the follow-up clock
      // that counts contact with the homeowner.
      internal
      connectedAs={account?.email_address ?? null}
      senders={senders}
      ownSender={own}
      sendLabel={t("Send work order")}
      documents={[
        {
          filename,
          mimeType: "application/pdf",
          label: withPricing
            ? t("the work order, with what the crew is paid")
            : t("the work order, without prices"),
          build: () => workOrderPdfBase64(pdf()),
        },
      ]}
      extraFields={
        <>
          {!to && (
            <div className="config-warn" role="status">
              {t(
                "{crew} has no email address on file. Add one in Tools → Crews, or type one in below — the day still went to them as a text.",
                { crew: crewLabel(crew) ?? t("This crew") },
              )}
            </div>
          )}
          {order.crew_emailed_at && (
            <div className="na-hint">
              {t("Last sent to {to} on {when}.", {
                to: order.crew_emailed_to ?? t("the crew"),
                when: fmtDateTime(order.crew_emailed_at),
              })}
            </div>
          )}
          <div className="field full">
            <label htmlFor="wo-pricing">{t("What the crew is paid")}</label>
            <span>
              <input
                id="wo-pricing"
                type="checkbox"
                checked={withPricing}
                onChange={(e) => setWithPricing(e.target.checked)}
              />{" "}
              {t("Put the prices on their copy")}
            </span>
          </div>
        </>
      }
      onClose={onClose}
      onSent={async (sent) => {
        await onSent(sent.to);
      }}
    />
  );
}
